/**
 * Sprint Insights — pure, deterministic computation of a sprint's health
 * picture and a few plain-language recommendations, composed from the Phase-1
 * aggregation + capacity engines.
 *
 * Everything here is computed LOCALLY (no I/O, no AI): the numbers are always
 * trustworthy and explainable. An optional AI narrative (see
 * {@link ./sprintNarrative}) can layer prose ON TOP of these numbers, but never
 * replaces them.
 *
 * Pure TS — no React, no I/O.
 */

import type { Task } from '../../tasks/types';
import {
  sprintHealth,
  sprintProgress,
  type SprintHealth,
} from '../engine/aggregation';
import { blockedForDays } from '../models/sprintboardModel';
import { unplannedScope } from '../../tasks/taskModel';

/** A blocked task surfaced in the insights, with how long it has been blocked. */
export interface BlockedInsight {
  taskId: string;
  title: string;
  days: number;
  reason?: string;
}

/** The full, locally-computed sprint insights payload. */
export interface SprintInsights {
  /** on_track / at_risk verdict (from the aggregation engine). */
  health: SprintHealth;
  /** Total committed effort in ideal days. */
  committedEffortDays: number;
  /** Completed (DONE) effort in ideal days. */
  completedEffortDays: number;
  /** Remaining (not-DONE) effort in ideal days. */
  remainingEffortDays: number;
  /** Whole-percent completion of committed effort. */
  percentComplete: number;
  /** Total team capacity in ideal days (0 when no team is linked). */
  teamCapacityDays: number;
  /** Remaining team capacity in ideal days (members × remaining working days). */
  remainingCapacityDays: number;
  /** Count of currently-blocked, not-DONE tasks. */
  blockedCount: number;
  /**
   * Count of "at-risk" tasks: not-DONE tasks blocked for {@link AT_RISK_BLOCKED_DAYS}
   * days or longer (a long-standing blocker is the sharpest risk signal).
   */
  atRiskCount: number;
  /** The blocked tasks (longest-blocked first) for surfacing detail. */
  blocked: BlockedInsight[];
  /**
   * Count of tasks pulled into the sprint mid-flight as unplanned/ad-hoc scope
   * (see {@link Task.adhoc}). 0 when the sprint has no added scope.
   */
  unplannedCount: number;
  /** Ideal-days of effort added by those unplanned tasks. */
  unplannedEffortDays: number;
  /** A few short, plain-language recommendations (may be empty). */
  recommendations: string[];
}

/** A not-DONE task blocked this many days or longer is treated as at-risk. */
export const AT_RISK_BLOCKED_DAYS = 2;

/** Rounds to 2 decimals. */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function isDone(task: Task): boolean {
  return task.boardStatus === 'DONE';
}

/**
 * Computes the sprint's insights from its tasks and the team/timeline context.
 *
 * @param tasks                the sprint's tasks (full Task, for blocker detail)
 * @param memberCount          engineers on the sprint's linked team
 * @param remainingWorkingDays working days left in the sprint
 * @param now                  injectable clock for deterministic tests
 */
export function buildSprintInsights(
  tasks: Task[],
  memberCount: number,
  remainingWorkingDays: number,
  now: Date = new Date(),
): SprintInsights {
  const list = tasks ?? [];
  const progress = sprintProgress(list);
  const health = sprintHealth(list, memberCount, remainingWorkingDays);

  const blocked: BlockedInsight[] = list
    .filter((t) => t.blocked && !isDone(t))
    .map((t) => ({
      taskId: t.id,
      title: t.title,
      days: blockedForDays(t.blocker?.since, now),
      reason: t.blocker?.reason,
    }))
    .sort((a, b) => b.days - a.days);

  const atRiskCount = blocked.filter(
    (b) => b.days >= AT_RISK_BLOCKED_DAYS,
  ).length;

  const remainingEffortDays = round2(
    progress.committedDays - progress.completedDays,
  );

  const unplanned = unplannedScope(list);

  const recommendations = buildRecommendations({
    health: health.health,
    blocked,
    remainingEffortDays,
    remainingCapacityDays: health.remainingCapacityDays,
    hasTeam: memberCount > 0,
  });

  return {
    health: health.health,
    committedEffortDays: progress.committedDays,
    completedEffortDays: progress.completedDays,
    remainingEffortDays,
    percentComplete: progress.percentComplete,
    teamCapacityDays: round2(memberCount * Math.max(0, remainingWorkingDays)),
    remainingCapacityDays: health.remainingCapacityDays,
    blockedCount: health.blockedCount,
    atRiskCount,
    blocked,
    unplannedCount: unplanned.count,
    unplannedEffortDays: unplanned.days,
    recommendations,
  };
}

/**
 * Builds the plain-language recommendations. Deliberately short and specific:
 * long-standing blockers first, then a work-vs-capacity read, then a healthy
 * buffer callout when everything looks fine.
 */
function buildRecommendations(args: {
  health: SprintHealth;
  blocked: BlockedInsight[];
  remainingEffortDays: number;
  remainingCapacityDays: number;
  hasTeam: boolean;
}): string[] {
  const out: string[] = [];

  // 1) Long-standing blockers get named, oldest first.
  for (const b of args.blocked) {
    if (b.days >= AT_RISK_BLOCKED_DAYS) {
      out.push(
        `“${b.title}” has been blocked for ${b.days} day${b.days === 1 ? '' : 's'} — escalate or find a way around it.`,
      );
    }
    if (out.length >= 2) break; // don't flood; the panel lists the rest.
  }

  // 2) Remaining work vs remaining capacity (only meaningful with a team).
  if (args.hasTeam) {
    const buffer = round2(args.remainingCapacityDays - args.remainingEffortDays);
    if (buffer < 0) {
      out.push(
        `Remaining work (${args.remainingEffortDays}d) exceeds remaining capacity (${args.remainingCapacityDays}d) by ${Math.abs(buffer)}d — descope or pull in help.`,
      );
    } else if (buffer <= 1 && args.remainingEffortDays > 0) {
      out.push(
        `Remaining work is right at capacity (${args.remainingEffortDays}d of ${args.remainingCapacityDays}d) — protect the sprint from new scope.`,
      );
    } else if (args.remainingEffortDays > 0) {
      out.push(
        `On pace: ${args.remainingEffortDays}d of work against ${args.remainingCapacityDays}d of remaining capacity (${buffer}d buffer).`,
      );
    }
  } else if (args.remainingEffortDays > 0) {
    out.push(
      `Link a team to this sprint to track ${args.remainingEffortDays}d of remaining work against capacity.`,
    );
  }

  // 3) A clean sprint deserves a clear "all good".
  if (out.length === 0) {
    out.push(
      args.health === 'on_track'
        ? 'No blockers and work is within capacity — the sprint is on track.'
        : 'No specific recommendations right now.',
    );
  }
  return out;
}
